import { useMemo } from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { StationeryTemplate } from './DesignsGallery.types';
import type { GalleryTemplate } from './components/GalleryItem/GalleryItem';
import type { ColorwayColorHit } from './useColorwayColorHits';
import { EMPTY_COLORWAY_ORDER } from './useColorwayColorHits';
import type { ColorwayOrderSource } from './GalleryColorwayOrderContext.types';
import type { PriceValuePropAsset } from './propValues.types';
import { useTagFilteredTemplates } from './useTagFilteredTemplates';
import { useFuse } from './components/Search/useFuse';
import { useSearchFilteredGalleryItems } from './components/Search/useSearchFilteredGalleryItems';
import { useThemeDisplayOrder, usePriorityThemeId } from './useDesignTemplates';
import { expandTemplatesByDisplayOrder } from './utils';
import { useGalleryItemsWithValueProps } from './useGalleryItemsWithValueProps';
import { useStationeryPagination } from './useStationeryPagination';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';

const isGalleryPageSizePayload = (payload: unknown): payload is number => {
  return typeof payload === 'number' && payload > 0;
};

type UseDesignGalleryItemsArgs = Readonly<{
  templates: ReadonlyArray<StationeryTemplate>;
  colorwayColorHits: ReadonlyArray<ColorwayColorHit>;
}>;

/**
 * Derives visible tiles and their matching colorway/search context from the catalog.
 * Must run inside DesignGalleryFiltersProvider and the gallery's search context.
 * Flow: facet filtering → search ranking → display-order expansion → promos → pagination → price tile.
 */
export const useDesignGalleryItems = ({ templates, colorwayColorHits }: UseDesignGalleryItemsArgs) => {
  const { templates: tagFilteredTemplates, colorwayOrder: filterColorwayOrder, isColorFilterActive, isStyleFilterActive } = useTagFilteredTemplates(templates, colorwayColorHits);
  const fuse = useFuse(templates);
  const tagFilteredItems: ReadonlyArray<GalleryTemplate> = useMemo(
    () =>
      tagFilteredTemplates.map(template => ({
        ...template,
        type: 'template',
      })),
    [tagFilteredTemplates],
  );
  const { isActive: isSearchActive, filteredItems, colorwayOrder: searchColorwayOrder, scoresByItem: searchScoresByItem } = useSearchFilteredGalleryItems(tagFilteredItems, fuse);

  // Search already scores every colorway, so when a term is active its ranking
  // supersedes the filter's (coarser) color ordering rather than competing with it.
  const colorwayOrder = useMemo((): Readonly<{ order: ReadonlyMap<string, ReadonlyArray<string>>; source?: ColorwayOrderSource }> => {
    if (isSearchActive) return { order: searchColorwayOrder, source: 'search' };
    if (isColorFilterActive) return { order: filterColorwayOrder, source: 'colorFilter' };
    return { order: EMPTY_COLORWAY_ORDER };
  }, [isSearchActive, searchColorwayOrder, filterColorwayOrder, isColorFilterActive]);

  // Without search, color, or style filtering, expand the list per the gallery-order
  // feature flag: a theme listed multiple times renders as multiple tiles, each
  // pre-selecting the flag's `+colorway`. Both a search term and a color filter
  // ignore the flag — they each pick the colorway a tile should show, which would
  // collapse the flag's deliberately-different repeats into identical tiles.
  const themeOrder = useThemeDisplayOrder();
  const priorityThemeId = usePriorityThemeId();
  const isColorwayNarrowed = isSearchActive || isColorFilterActive || isStyleFilterActive;
  const galleryItems = useMemo(
    () => (!isColorwayNarrowed && themeOrder?.length ? expandTemplatesByDisplayOrder(filteredItems as ReadonlyArray<GalleryTemplate>, themeOrder, priorityThemeId) : filteredItems),
    [isColorwayNarrowed, themeOrder, filteredItems, priorityThemeId],
  );

  const { payload: galleryPageSizePayload } = useFeatureValue('printGalleryPageSize');
  // A valid flag payload overrides the pagination hook's 100-item default, including an existing value of 50.
  const ffPageSize = isGalleryPageSizePayload(galleryPageSizePayload) ? galleryPageSizePayload : undefined;

  const { itemsWithProps, galleryColumnCount } = useGalleryItemsWithValueProps(galleryItems, isSearchActive);
  const { items: paginatedTemplates, ...pagination } = useStationeryPagination(itemsWithProps, galleryColumnCount, isSearchActive, ffPageSize);

  const firstTemplate = galleryItems.find((item): item is GalleryTemplate => item.type === 'template');

  const { getTotalActiveFilterCount } = useDesignGalleryFiltersContext();

  const paginatedItems = (() => {
    if (!firstTemplate || paginatedTemplates.length === 0 || isSearchActive || pagination.page > 1 || getTotalActiveFilterCount() > 0) return paginatedTemplates;
    const priceTile: PriceValuePropAsset & { type: 'priceValueProp' } = {
      type: 'priceValueProp',
      representativeTemplate: firstTemplate,
    };
    const insertAt = Math.min(galleryColumnCount, paginatedTemplates.length);
    return [...paginatedTemplates.slice(0, insertAt), priceTile, ...paginatedTemplates.slice(insertAt)];
  })();

  return {
    items: paginatedItems,
    pagination,
    hasNoSearchResults: isSearchActive && !filteredItems.length,
    colorwayOrder,
    searchScoresByItem,
  };
};
